import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  addLoggedInUserAclToDrive,
  getSharedDriveById,
  UserKeys,
} from '../../../../../../api/users-api';
import SharedDialog from '../../../../../../shared/shared-dialog';
import { handleErrors } from '../../../../../../utils/errors';
import { useStore } from '../../../../../state/zustand-store';
import { useAnalytics } from '../../../../../../shared/analytics/create-events';
import { ModuleList } from '../../../../../../shared/analytics/module-list';
import * as analytics from '../../../../../../shared/analytics/analytics.json';
import { Messages } from '../../../../../../utils/message-constants';
import { useUserSettings } from '../../../../../../utils/use-user-settings';

import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

interface SharedDrivesPermissionCheckDialogProps {
  folderOrDriveId: string;
  open: boolean;
  handleClose: () => void;
  minimumRequiredRole:
    | 'reader'
    | 'commenter'
    | 'writer'
    | 'fileOrganizer'
    | 'organizer';
  actionAfterPermissionGranted: () => void;
  nameOfSharedDrive: string | null;
}

export default function SharedDrivesPermissionCheckDialog({
  open,
  handleClose,
  minimumRequiredRole,
  folderOrDriveId,
  actionAfterPermissionGranted,
  nameOfSharedDrive,
}: SharedDrivesPermissionCheckDialogProps) {
  const { setSnackbar } = useStore();
  const queryClient = useQueryClient();
  const { createEvent } = useAnalytics();
  const { loggedInUser } = useUserSettings();

  const roleArray = [
    { role: 'reader', name: 'Viewer' },
    { role: 'commenter', name: 'Commenter' },
    { role: 'writer', name: 'Contributor' },
    { role: 'fileOrganizer', name: 'Content Manager' },
    { role: 'organizer', name: 'Manager' },
  ];

  const roleIndex = roleArray.findIndex(
    (role) => role.role === minimumRequiredRole
  );

  const displayedRoles = roleArray.slice(roleIndex);

  const [role, setRole] = useState(roleArray[roleIndex].role);

  const url = window.location.href.toLowerCase();
  const isSharedDrives = url.includes('shared');
  const isDriveExplorer = url.includes('explorer');

  const hasName = nameOfSharedDrive !== null && nameOfSharedDrive !== '';

  const { data, isLoading } = useQuery({
    queryKey: [UserKeys.SharedDrive, folderOrDriveId],
    queryFn: () => getSharedDriveById(folderOrDriveId),
    enabled: !hasName,
  });

  const { mutate, isPending } = useMutation({
    mutationFn: () => addLoggedInUserAclToDrive(folderOrDriveId, role),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Added('Permission'),
      });

      queryClient.setQueryData(
        [UserKeys.SharedDriveUserPermission, loggedInUser, folderOrDriveId],
        role
      );

      handleClose();
      actionAfterPermissionGranted();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Adding('the permission'),
      });
    },
    onSettled: () => {
      if (isSharedDrives) {
        createEvent(analytics.drive.shared_drives.content.update_drive_shares, {
          module: ModuleList.modules.drive.name,
          section: ModuleList.modules.drive.sections.sharedDrives,
        });
      } else if (isDriveExplorer) {
        createEvent(
          analytics.drive.drive_explorer.shared_drives.content
            .update_drive_shares,
          {
            module: ModuleList.modules.drive.name,
            section: ModuleList.modules.drive.sections.driveExplorer,
          }
        );
      } else {
        createEvent(
          analytics.user_management.user_details.drive.shared_drives.content
            .update_drive_shares,
          {
            module: ModuleList.modules.userManagement.name,
            section: ModuleList.modules.userManagement.sections.drive,
          }
        );
      }
    },
  });

  function handleSubmit() {
    mutate();
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title={`Access Request`}
      actingAs={loggedInUser}
      dialogContent={
        <Box>
          <Typography>
            You do not have access to perform this action. Add yourself to the
            Shared Drive: "
            {hasName ? (
              nameOfSharedDrive
            ) : isLoading ? (
              <Skeleton
                variant="text"
                width={100}
                sx={{ display: 'inline-block' }}
              />
            ) : (
              data?.name || ''
            )}
            " as at least {roleArray[roleIndex].name} to continue.
          </Typography>
          <TextField
            fullWidth
            autoFocus
            value={role}
            onChange={(e) => setRole(e.target.value)}
            select
            sx={{ mt: 2 }}
          >
            {displayedRoles.map((role) => (
              <MenuItem key={role.role} value={role.role}>
                {role.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      }
      primaryButton={{
        title: 'Save Changes',
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
      }}
    />
  );
}
